import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import logger from './utils/logger';

// Get the root element
const container = document.getElementById('root');
if (!container) {
  throw new Error('Failed to find the root element');
}

// Initialize performance monitoring
const startTime = performance.now();

// Create root and render app
const root = createRoot(container);

// Filter out SES/lockdown warnings from browser extensions in development
if (import.meta.env.DEV) {
  const originalWarn = console.warn;
  console.warn = (...args) => {
    // Filter out SES lockdown warnings from MetaMask and similar extensions
    if (args[0]?.includes?.('SES') || args[0]?.includes?.('lockdown') || args[0]?.includes?.('intrinsics')) {
      return;
    }
    originalWarn(...args);
  };
}

// Error handling for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled promise rejection', {
    reason: event.reason,
    promise: event.promise,
  });
});

// Error handling for unhandled errors
window.addEventListener('error', (event) => {
  logger.error('Unhandled error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error?.stack,
  });
});

// Log application initialization
logger.info('Application initializing', {
  userAgent: navigator.userAgent,
  url: window.location.href,
  viewport: {
    width: window.innerWidth,
    height: window.innerHeight,
  },
  timestamp: new Date().toISOString(),
});

// Render the application
try {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  // Log successful initialization
  const initTime = performance.now() - startTime;
  logger.info('Application initialized successfully', {
    initialization_time_ms: Math.round(initTime),
  });
} catch (error) {
  logger.error('Failed to render application', {
    error: error instanceof Error ? error.message : 'Unknown error',
    stack: error instanceof Error ? error.stack : undefined,
  });

  // Fallback error display
  container.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      font-family: Arial, sans-serif;
      text-align: center;
      padding: 20px;
    ">
      <h1 style="color: #d32f2f;">Application Failed to Load</h1>
      <p style="color: #666; margin-bottom: 20px;">
        We're sorry, but the application failed to initialize. 
        Please try refreshing the page or contact support if the problem persists.
      </p>
      <button 
        onclick="window.location.reload()" 
        style="
          background-color: #1976d2;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
        "
      >
        Refresh Page
      </button>
      <div style="
        margin-top: 20px;
        padding: 10px;
        background-color: #f5f5f5;
        border-radius: 4px;
        max-width: 500px;
        font-family: monospace;
        font-size: 12px;
        text-align: left;
        word-break: break-all;
      ">
        Error: ${error instanceof Error ? error.message : 'Unknown error'}
      </div>
    </div>
  `;
}

// Web Vitals reporting (optional)
const reportWebVitals = (onPerfEntry?: (metric: any) => void) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    }).catch((error) => {
      logger.warn('Web Vitals import failed', {
        error: error.message,
      });
    });
  }
};

// Start performance monitoring in development
if (import.meta.env.DEV) {
  reportWebVitals((metric) => {
    logger.debug('Web Vitals metric', {
      name: metric.name,
      value: metric.value,
      id: metric.id,
    });
  });
}